// SupplierIntelligenceAgent.tsx
// A05 Supplier Intelligence Agent — Supplementary Brief D2
// Three trigger points for procurement intelligence:
//   1. Spec Compliance Check  — is this product compliant with the client spec?
//   2. Lead Time Analysis     — will materials arrive before scheduled task dates?
//   3. Supplier Ranking       — which supplier is best for this material category?
//
// The spec compliance check calls the Anthropic API live (same pattern as XeroIntegration).
// Lead time and ranking use structured mock data from mockDataSuppliers.ts.

import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_SUPPLIERS, SUPPLIER_PRODUCTS, getPriceDriftProducts,
  SupplierProduct, SUPPLIER_CATEGORY_META, SupplierCategory,
} from '../utils/mockDataSuppliers'
import { MOCK_AGENT_OUTPUTS } from '../utils/mockDataAgents'
import {
  Sparkles, AlertTriangle, CheckCircle, Clock, Search,
  TrendingUp, TrendingDown, Minus, RefreshCw, Info,
  ShoppingCart, BarChart3, Package, Star, ChevronRight,
} from 'lucide-react'

// ── Types ──────────────────────────────────────────────────────────────────

type ComplianceStatus = 'COMPLIANT' | 'NON_COMPLIANT' | 'CONDITIONAL' | 'APPROVED_ALTERNATIVE' | 'UNKNOWN'
type Tab = 'intel' | 'products' | 'lead-time' | 'ranking'

interface ComplianceResult {
  compliant:   boolean
  status:      ComplianceStatus
  specRef:     string
  reason:      string
  alternative: string | null
  checkDate:   string
}

// ── Seed lead-time data (tasks with schedule dates) ───────────────────────

const SCHEDULED_TASKS = [
  { id: 'task-1', jobNumber: 'TP-2026-001', description: 'HV cable termination Pit 4B', scheduledDate: new Date(Date.now() + 2*86400000).toISOString().split('T')[0], materialsRequired: ['Signal cable CU 6mm²', 'Cable glands 50mm', 'Conduit 50mm'] },
  { id: 'task-2', jobNumber: 'TP-2026-003', description: 'TC officer deployment — intersection', scheduledDate: new Date(Date.now() + 3*86400000).toISOString().split('T')[0], materialsRequired: ['Traffic cones 700mm × 48', 'WORKS AHEAD signs × 4'] },
  { id: 'task-3', jobNumber: 'TP-2026-003', description: 'Signal pole erection — night shift', scheduledDate: new Date(Date.now() + 7*86400000).toISOString().split('T')[0], materialsRequired: ['LED signal heads × 4', 'Ready-mix concrete N32 × 0.8m³'] },
  { id: 'task-4', jobNumber: 'TP-2026-004', description: 'Conduit trench backfill', scheduledDate: new Date(Date.now() + 5*86400000).toISOString().split('T')[0], materialsRequired: ['DGB20 basecourse × 12t'] },
]

// ── Helpers ────────────────────────────────────────────────────────────────

const fmtCurrency = (n: number) => `$${n.toFixed(2)}`
const fmt = (s: string) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
const daysUntil = (iso: string) => Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000)

function deliveryBadge(rate: number) {
  const cls = rate >= 90 ? 'text-green-700 bg-green-100' : rate >= 80 ? 'text-amber-700 bg-amber-100' : 'text-red-700 bg-red-100'
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{rate}% OTD</span>
}

function priceDriftBadge(product: SupplierProduct) {
  if (!product.estimateRate) return null
  const pct = ((product.unitPrice - product.estimateRate) / product.estimateRate * 100)
  if (Math.abs(pct) < 2) return <span className="text-[11px] text-gray-400 flex items-center gap-0.5"><Minus className="w-3 h-3" />On target</span>
  const up = pct > 0
  return (
    <span className={`text-[11px] font-semibold flex items-center gap-0.5 ${up ? 'text-red-600' : 'text-green-600'}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? '+' : ''}{pct.toFixed(1)}%
    </span>
  )
}

function complianceBadge(status: ComplianceStatus) {
  const map: Record<ComplianceStatus, { label: string; cls: string }> = {
    COMPLIANT:           { label: '✓ Compliant',          cls: 'bg-green-100 text-green-700' },
    NON_COMPLIANT:       { label: '✗ Non-compliant',       cls: 'bg-red-100 text-red-700' },
    CONDITIONAL:         { label: '⚠ Conditional',         cls: 'bg-amber-100 text-amber-700' },
    APPROVED_ALTERNATIVE:{ label: '↔ Alt. product',        cls: 'bg-blue-100 text-blue-700' },
    UNKNOWN:             { label: '? Unknown',             cls: 'bg-gray-100 text-gray-600' },
  }
  const m = map[status]
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${m.cls}`}>{m.label}</span>
}

// ── Anthropic API call ─────────────────────────────────────────────────────

async function checkProductCompliance(
  product: string,
  clientSpec: string,
  jobRef: string
): Promise<ComplianceResult | null> {
  try {
    const prompt = `You are A05 Supplier Intelligence Agent for MCB Civil Electrical, a civil and electrical contractor specialising in roadside ITS infrastructure for the Queensland Department of Transport and Main Roads (TMR).

Check if this product meets the required specification for the given project context.

Product description: ${product}
Required client specification: ${clientSpec}
Project reference: ${jobRef}

Queensland TMR material standards to reference where relevant:
- MRTS52: Signal cable and conduit (orange sheath for buried cable, AS/NZS 5000.1)
- AS/NZS 4191: Traffic signal equipment (LED signal heads must have TMR type approval)
- AS/NZS 4602.1: Traffic control devices (retroreflective sheeting Class 1 minimum)
- MRTS70: Concrete for signal pole foundations (N32 minimum)
- MRTS05: Pavement subbase materials
- MUTCD Part 3: Works on roads — sign and device requirements

Respond with ONLY valid JSON (no markdown, no backticks):
{"compliant":true/false,"status":"COMPLIANT"|"NON_COMPLIANT"|"CONDITIONAL"|"APPROVED_ALTERNATIVE","specRef":"e.g. MRTS52 Cl.4.3","reason":"one clear sentence explaining compliance status","alternative":"specific compliant alternative if non-compliant, null otherwise","checkDate":"${new Date().toISOString().split('T')[0]}"}`

    const r = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model:      'claude-sonnet-4-6',
        max_tokens: 400,
        messages:   [{ role: 'user', content: prompt }],
      }),
    })
    const d = await r.json()
    const text = (d.content?.[0]?.text ?? '').trim().replace(/```json|```/g, '')
    return JSON.parse(text) as ComplianceResult
  } catch {
    return null
  }
}

// ── Main component ─────────────────────────────────────────────────────────

export function SupplierIntelligenceAgent() {
  const { isTier } = useAuthStore()

  const [tab, setTab]               = useState<Tab>('intel')
  const [productInput, setProductInput] = useState('')
  const [specInput, setSpecInput]   = useState('')
  const [jobRef, setJobRef]         = useState('TP-2026-001')
  const [checking, setChecking]     = useState(false)
  const [result, setResult]         = useState<ComplianceResult | null>(null)
  const [checkError, setCheckError] = useState('')
  const [catFilter, setCatFilter]   = useState<SupplierCategory | 'ALL'>('ALL')
  const [search, setSearch]         = useState('')

  const output = MOCK_AGENT_OUTPUTS['procurement']
  const driftProducts = getPriceDriftProducts(5)

  // Product catalogue filter
  const filteredProducts = useMemo(() => SUPPLIER_PRODUCTS.filter(p => {
    const mCat = catFilter === 'ALL' || p.category === catFilter
    const mSearch = !search || p.description.toLowerCase().includes(search.toLowerCase()) || p.productCode.toLowerCase().includes(search.toLowerCase())
    return mCat && mSearch
  }), [catFilter, search])

  // Lead-time analysis: which tasks have potential conflicts?
  const leadTimeFlags = useMemo(() => SCHEDULED_TASKS.map(task => {
    const days = daysUntil(task.scheduledDate)
    // Find matching products and their lead times
    const issues: string[] = []
    task.materialsRequired.forEach(mat => {
      const matching = SUPPLIER_PRODUCTS.filter(p =>
        p.description.toLowerCase().includes(mat.toLowerCase().split(' ')[0].toLowerCase())
      )
      if (matching.length === 0) { issues.push(`${mat} — no supplier product found`) }
      else {
        const bestLead = Math.min(...matching.map(p => p.standardLeadDays))
        if (bestLead >= days) issues.push(`${mat.split(' ')[0]} — lead time ${bestLead}d vs ${days}d until task`)
      }
    })
    return { ...task, daysUntil: days, issues }
  }), [])

  // Supplier ranking per category
  const topSuppliersByCategory = useMemo(() => {
    const cats = ['ELECTRICAL_SIGNALS', 'TRAFFIC_CONTROL', 'CIVIL_MATERIALS'] as SupplierCategory[]
    return cats.map(cat => {
      const sups = MOCK_SUPPLIERS.filter(s => s.status === 'ACTIVE' && s.categories.includes(cat))
        .sort((a, b) => (b.rating * 20 + (b.isPreferred ? 20 : 0)) - (a.rating * 20 + (a.isPreferred ? 20 : 0)))
        .slice(0, 3)
      return { cat, suppliers: sups }
    })
  }, [])

  const handleCheck = async () => {
    if (!productInput.trim() || !specInput.trim()) return
    setChecking(true)
    setResult(null)
    setCheckError('')
    const r = await checkProductCompliance(productInput.trim(), specInput.trim(), jobRef)
    setChecking(false)
    if (r) setResult(r)
    else setCheckError('Unable to connect to A05. Check API connection or try again.')
  }

  const tabs: { key: Tab; label: string; badge?: number }[] = [
    { key: 'intel',     label: 'Agent intelligence',  badge: (output.urgent.length + output.warning.length) || undefined },
    { key: 'products',  label: 'Product catalogue',   badge: driftProducts.length || undefined },
    { key: 'lead-time', label: 'Lead time analysis',  badge: leadTimeFlags.filter(t => t.issues.length > 0).length || undefined },
    { key: 'ranking',   label: 'Supplier ranking' },
  ]

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            A05 — Supplier Intelligence Agent
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Procurement intelligence at three trigger points: BOM compliance · lead time conflict · supplier ranking
          </p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-xs text-green-800 font-medium flex items-center gap-1.5">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />Active — Wave 1
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t.label}
            {t.badge ? <span className="ml-1 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{t.badge}</span> : null}
          </button>
        ))}
      </div>

      {/* ── TAB: Agent Intelligence ── */}
      {tab === 'intel' && (
        <div className="space-y-5">

          {/* Spec Compliance Checker (LIVE Anthropic API) */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-purple-700 text-white px-5 py-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <div>
                <div className="font-semibold text-sm">Live spec compliance check</div>
                <div className="text-xs text-purple-200">A05 checks your product against the client's technical specification using live AI analysis</div>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="sm:col-span-1">
                  <label className="label-sm">Product description</label>
                  <input value={productInput} onChange={e => setProductInput(e.target.value)}
                    placeholder="e.g. PVC conduit 50mm grey sheath"
                    className="input text-sm w-full" />
                </div>
                <div className="sm:col-span-1">
                  <label className="label-sm">Required specification</label>
                  <input value={specInput} onChange={e => setSpecInput(e.target.value)}
                    placeholder="e.g. MRTS52 Cl.4.3 buried signal cable"
                    className="input text-sm w-full" />
                </div>
                <div>
                  <label className="label-sm">Job reference</label>
                  <input value={jobRef} onChange={e => setJobRef(e.target.value)}
                    className="input text-sm w-full" />
                </div>
              </div>

              <div className="flex gap-3 items-center">
                <button onClick={handleCheck} disabled={checking || !productInput.trim() || !specInput.trim()}
                  className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40">
                  {checking
                    ? <><RefreshCw className="w-4 h-4 animate-spin" />A05 analysing…</>
                    : <><Sparkles className="w-4 h-4" />Run compliance check</>}
                </button>
                <p className="text-xs text-gray-400">Calls Claude claude-sonnet-4-6 via Anthropic API · 5–10 seconds</p>
              </div>

              {checkError && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />{checkError}
                </div>
              )}

              {result && (
                <div className={`rounded-xl border p-5 space-y-3 ${result.compliant ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-300'}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="font-semibold text-gray-900 text-sm">{productInput}</div>
                    {complianceBadge(result.status)}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-gray-500 mb-0.5">Specification reference</div>
                      <div className="font-medium text-gray-800 font-mono text-xs bg-gray-100 px-2 py-1 rounded">{result.specRef}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-0.5">Check date</div>
                      <div className="text-xs text-gray-600">{result.checkDate}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">A05 analysis</div>
                    <p className="text-sm text-gray-800">{result.reason}</p>
                  </div>
                  {result.alternative && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-800">
                      <strong>Compliant alternative:</strong> {result.alternative}
                    </div>
                  )}
                </div>
              )}

              {/* Quick-fill examples */}
              {!result && !checking && (
                <div className="flex gap-2 flex-wrap">
                  <p className="text-xs text-gray-400 mr-1">Try:</p>
                  {[
                    { prod: 'PVC cable 6mm² grey sheath', spec: 'MRTS52 Cl.4.3 buried signal cable' },
                    { prod: 'LED signal head AS/NZS 4191 TMR type-approved', spec: 'AS/NZS 4191 ITS traffic signal' },
                    { prod: 'Traffic cone 700mm orange retroreflective', spec: 'MUTCD Part 3 delineation devices' },
                  ].map(ex => (
                    <button key={ex.prod} onClick={() => { setProductInput(ex.prod); setSpecInput(ex.spec) }}
                      className="text-xs text-purple-600 hover:text-purple-800 hover:underline">
                      {ex.prod}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Agent alerts */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: 'Urgent',   items: output.urgent,   bg: 'bg-red-50',    border: 'border-red-200',   text: 'text-red-700',   dot: 'bg-red-500' },
              { label: 'Warnings', items: output.warning,  bg: 'bg-amber-50',  border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
              { label: 'Advisory', items: output.advisory, bg: 'bg-blue-50',   border: 'border-blue-200',  text: 'text-blue-700',  dot: 'bg-blue-500' },
            ].map(({ label, items, bg, border, text, dot }) => (
              <div key={label} className={`rounded-xl border ${bg} ${border} p-4 space-y-3`}>
                <div className={`text-xs font-bold uppercase tracking-wide flex items-center gap-1.5 ${text}`}>
                  <span className={`w-2 h-2 rounded-full ${dot}`} />{label} ({items.length})
                </div>
                {items.length === 0
                  ? <p className="text-xs text-gray-400 italic">No {label.toLowerCase()} items</p>
                  : items.map(item => (
                    <div key={item.id} className="space-y-1">
                      <div className={`text-xs font-semibold ${text}`}>{item.title}</div>
                      <p className="text-xs text-gray-600 leading-relaxed">{item.detail}</p>
                      <div className="text-xs text-gray-500 font-medium">→ {item.action}</div>
                      {item.clause && <div className="text-[10px] text-gray-400 font-mono">{item.clause}</div>}
                    </div>
                  ))
                }
              </div>
            ))}
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-500">
            <strong>A05 summary:</strong> {output.summary}
          </div>
        </div>
      )}

      {/* ── TAB: Product Catalogue ── */}
      {tab === 'products' && (
        <div className="space-y-4">
          {driftProducts.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
              <TrendingUp className="w-4 h-4 flex-shrink-0 text-amber-600" />
              <span><strong>{driftProducts.length} product{driftProducts.length !== 1 ? 's' : ''} with price drift ≥5% vs estimate.</strong> Review unit rates in Live Budget before next PO is raised.</span>
            </div>
          )}

          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products…" className="input pl-9 text-sm w-full" />
            </div>
            <select value={catFilter} onChange={e => setCatFilter(e.target.value as any)} className="input text-sm w-48">
              <option value="ALL">All categories</option>
              {Object.entries(SUPPLIER_CATEGORY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Product', 'Supplier', 'Unit price', 'vs Estimate', 'Spec', 'Lead', 'OTD', 'Clients'].map(h => (
                    <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProducts.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 text-xs">{p.description}</div>
                      <div className="text-[10px] text-gray-400 font-mono mt-0.5">{p.productCode}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{p.supplierName}{p.supplierId === MOCK_SUPPLIERS.find(s => s.id === p.supplierId && s.isPreferred)?.id ? <span className="ml-1 text-[10px] text-yellow-600">★</span> : null}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-900">{fmtCurrency(p.unitPrice)}/{p.unit}</td>
                    <td className="px-4 py-3">{priceDriftBadge(p)}</td>
                    <td className="px-4 py-3">
                      {p.isApprovedProduct
                        ? <span className="text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">✓ {p.clientSpecCode ?? 'Approved'}</span>
                        : <span className="text-[10px] font-semibold text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full">✗ Not approved</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{p.standardLeadDays}d{p.expressLeadDays ? ` / ${p.expressLeadDays}d exp` : ''}</td>
                    <td className="px-4 py-3">{deliveryBadge(p.onTimeDeliveryRate)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{p.approvedForClients.join(', ')}</td>
                  </tr>
                ))}
                {filteredProducts.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-8 text-gray-400 text-sm">No products match the filter</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TAB: Lead Time Analysis ── */}
      {tab === 'lead-time' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-2 text-sm text-blue-800">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
            A05 cross-references scheduled task dates against supplier lead times. A flag means the earliest available delivery date is after the task start date.
          </div>

          <div className="space-y-3">
            {leadTimeFlags.map(task => {
              const isRisk = task.issues.length > 0
              const urgent = task.daysUntil <= 3 && isRisk
              return (
                <div key={task.id} className={`bg-white border rounded-xl p-4 ${urgent ? 'border-red-300' : isRisk ? 'border-amber-300' : 'border-gray-200'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${urgent ? 'bg-red-100' : isRisk ? 'bg-amber-100' : 'bg-green-100'}`}>
                      {urgent ? <AlertTriangle className="w-4 h-4 text-red-600" />
                       : isRisk ? <Clock className="w-4 h-4 text-amber-600" />
                       : <CheckCircle className="w-4 h-4 text-green-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-1">
                        <span className="font-semibold text-sm text-gray-900">{task.description}</span>
                        <span className="text-xs text-gray-500">{task.jobNumber}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${urgent ? 'bg-red-100 text-red-700' : isRisk ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                          {task.daysUntil <= 0 ? 'Today' : `${task.daysUntil}d until start`} — {fmt(task.scheduledDate)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mb-2">Materials required: {task.materialsRequired.join(' · ')}</div>
                      {isRisk
                        ? <div className="space-y-1.5">
                            {task.issues.map((issue, i) => (
                              <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg ${urgent ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                                ⚠ {issue}
                              </div>
                            ))}
                          </div>
                        : <div className="text-xs text-green-700 bg-green-50 px-2.5 py-1.5 rounded-lg">
                            ✓ All materials can be delivered before task start date
                          </div>
                      }
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── TAB: Supplier Ranking ── */}
      {tab === 'ranking' && (
        <div className="space-y-5">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-2 text-sm text-blue-800">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
            A05 ranks suppliers by composite score: rating (40%), preferred status (20%), on-time delivery (25%), lead time (15%). Lower lead time is better.
          </div>

          {topSuppliersByCategory.map(({ cat, suppliers }) => (
            <div key={cat} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${SUPPLIER_CATEGORY_META[cat].bg} ${SUPPLIER_CATEGORY_META[cat].text}`}>
                  {SUPPLIER_CATEGORY_META[cat].label}
                </span>
              </div>
              <div className="divide-y divide-gray-100">
                {suppliers.map((sup, idx) => (
                  <div key={sup.id} className="flex items-center gap-4 px-5 py-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                      idx === 0 ? 'bg-yellow-100 text-yellow-700' : idx === 1 ? 'bg-gray-200 text-gray-600' : 'bg-orange-50 text-orange-700'
                    }`}>{idx + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-gray-900">{sup.tradingName ?? sup.companyName}</span>
                        {sup.isPreferred && <span className="text-[10px] bg-yellow-100 text-yellow-700 font-bold px-1.5 py-0.5 rounded-full">★ Preferred</span>}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Lead: {sup.leadTimeDays}d · Terms: {sup.paymentTermsDays}d · {sup.suburb}, {sup.state}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }, (_, i) => (
                          <Star key={i} className={`w-3.5 h-3.5 ${i < sup.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">{sup.contactName}</span>
                      <span className="text-xs text-gray-400 font-mono">{sup.contactDirect ?? sup.phone}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
